'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { toast } from 'sonner';

interface AddDogForm {
  name: string;
  breed: string;
  dob: string;
  gender: string;
  about: string;
}

const STEPS = ['Basic Info', 'About', 'Review'];

const POPULAR_BREEDS = [
  'Golden Retriever', 'Labrador Retriever', 'German Shepherd', 'French Bulldog',
  'Bulldog', 'Poodle', 'Beagle', 'Rottweiler', 'Dachshund', 'Shiba Inu',
  'Border Collie', 'Siberian Husky', 'Mixed Breed', 'Rescue Dog', 'Unknown Breed',
];

export default function AddDogModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [step, setStep] = useState(0);
  const { register, handleSubmit, watch, formState: { errors } } = useForm<AddDogForm>();
  const values = watch();

  const onSubmit = async () => {
    await new Promise(r => setTimeout(r, 800));
    toast.success(`${values.name || 'Your dog'}'s profile has been created!`);
    onClose();
    setStep(0);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-2xl shadow-modal w-full max-w-lg fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-border">
          <div>
            <h2 className="font-700 text-lg text-foreground">Add a Dog Profile</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Step {step + 1} of {STEPS.length} — {STEPS[step]}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Step progress */}
        <div className="flex gap-1 px-6 pt-4">
          {STEPS.map((s, i) => (
            <div key={`step-${s}`} className="flex-1 flex items-center gap-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-700 border-2 transition-all
                ${i < step ? 'bg-primary border-primary text-white' : i === step ? 'border-primary text-primary bg-primary/10' : 'border-border text-muted-foreground'}`}>
                {i < step ? <Check size={12} /> : i + 1}
              </div>
              <span className={`text-xs font-500 ${i === step ? 'text-foreground' : 'text-muted-foreground'}`}>{s}</span>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? 'bg-primary' : 'bg-border'}`} />}
            </div>
          ))}
        </div>

        {/* Step content */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="px-6 py-5 space-y-4 min-h-[280px]">
            {step === 0 && (
              <>
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1">Dog's Name <span className="text-danger">*</span></label>
                  <input
                    {...register('name', { required: "Please enter your dog's name" })}
                    className="w-full px-3 py-2.5 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="e.g. Biscuit, Max, Luna"
                  />
                  {errors.name && <p className="text-xs text-danger mt-1">{errors.name.message}</p>}
                </div>
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1">Breed <span className="text-danger">*</span></label>
                  <p className="text-xs text-muted-foreground mb-2">Enter any breed or mix. You can also type a custom breed.</p>
                  <input
                    {...register('breed', { required: 'Please enter a breed' })}
                    list="breed-list"
                    className="w-full px-3 py-2.5 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="e.g. Golden Retriever, Labrador x Poodle, Mixed Breed"
                  />
                  <datalist id="breed-list">
                    {POPULAR_BREEDS.map(b => <option key={`breed-${b}`} value={b} />)}
                  </datalist>
                  {errors.breed && <p className="text-xs text-danger mt-1">{errors.breed.message}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-600 text-foreground mb-1">Date of Birth</label>
                    <input
                      {...register('dob')}
                      type="date"
                      className="w-full px-3 py-2.5 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-600 text-foreground mb-1">Gender</label>
                    <select
                      {...register('gender')}
                      className="w-full px-3 py-2.5 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Select</option>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {step === 1 && (
              <div>
                <label className="block text-xs font-600 text-foreground mb-1">About {values.name || 'Your Dog'}</label>
                <p className="text-xs text-muted-foreground mb-2">Write a short introduction. This appears on the public profile.</p>
                <textarea
                  {...register('about')}
                  rows={6}
                  className="w-full px-3 py-2.5 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  placeholder={`Tell the world about ${values.name || 'your dog'}. Their personality, favorite things, quirks...`}
                />
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                <div className="bg-muted rounded-xl p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground">Name</span>
                    <span className="text-xs font-600 text-foreground">{values.name || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground">Breed</span>
                    <span className="text-xs font-600 text-foreground">{values.breed || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground">Date of Birth</span>
                    <span className="text-xs font-600 text-foreground">{values.dob || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground">Gender</span>
                    <span className="text-xs font-600 text-foreground capitalize">{values.gender || '—'}</span>
                  </div>
                </div>
                {values.about && (
                  <div className="bg-muted rounded-xl p-4">
                    <p className="text-xs text-muted-foreground mb-1">About</p>
                    <p className="text-sm text-foreground leading-relaxed">{values.about}</p>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">You can add photos and memories after creating the profile.</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 pb-6 pt-2 border-t border-border">
            <button
              type="button"
              onClick={() => step === 0 ? onClose() : setStep(s => s - 1)}
              className="flex items-center gap-1.5 px-4 py-2 border border-border rounded-lg text-sm font-600 text-foreground hover:bg-muted transition-colors"
            >
              <ChevronLeft size={15} />
              {step === 0 ? 'Cancel' : 'Back'}
            </button>
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={() => setStep(s => s + 1)}
                className="paw-btn flex items-center gap-1.5 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-700 hover:bg-secondary-foreground transition-all"
              >
                Next
                <ChevronRight size={15} />
              </button>
            ) : (
              <button
                type="submit"
                className="paw-btn flex items-center gap-1.5 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-700 hover:bg-secondary-foreground transition-all"
              >
                <Check size={15} />
                Create Profile
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}